import {
  BackstageUserIdentity,
  ConfigApi,
  DiscoveryApi,
  IdentityApi,
  ProfileInfo,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

type LocalAuthResponse = {
  profile: ProfileInfo;
  backstageIdentity: {
    token: string;
    identity: BackstageUserIdentity;
  };
};

type LocalAuthUiConfig = Pick<
  ConfigApi,
  'getOptionalBoolean' | 'getOptionalString'
>;

type LocalAuthRequestOptions = {
  username: string;
  password: string;
  discoveryApi: Pick<DiscoveryApi, 'getBaseUrl'>;
  fetch?: typeof fetch;
};

const TOKEN_REFRESH_MARGIN_MS = 60_000;

function isLoopbackUrl(value: string | undefined): boolean {
  if (!value) {
    return false;
  }

  try {
    return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(
      new URL(value).hostname,
    );
  } catch {
    return false;
  }
}

function getHttpsHostname(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  try {
    const url = new URL(value);
    return url.protocol === 'https:'
      ? url.hostname.toLocaleLowerCase()
      : undefined;
  } catch {
    return undefined;
  }
}

export function isLocalAuthUiEnabled(config: LocalAuthUiConfig): boolean {
  return (
    config.getOptionalBoolean('dhDock.localAuth.enabled') === true &&
    config.getOptionalString('auth.environment') === 'development' &&
    isLoopbackUrl(config.getOptionalString('app.baseUrl')) &&
    isLoopbackUrl(config.getOptionalString('backend.baseUrl'))
  );
}

export function isDcpDevelopmentAuthUiEnabled(
  config: LocalAuthUiConfig,
): boolean {
  const expectedHost = config
    .getOptionalString('dhDock.deployment.externalHost')
    ?.toLocaleLowerCase();

  return (
    config.getOptionalBoolean('dhDock.agentAuth.enabled') === true &&
    config.getOptionalString('auth.environment') === 'development' &&
    config.getOptionalString('dhDock.deployment.environment') ===
      'dcp-development' &&
    config.getOptionalBoolean('dhDock.deployment.initialAcceptance') === true &&
    Boolean(expectedHost) &&
    getHttpsHostname(config.getOptionalString('app.baseUrl')) ===
      expectedHost &&
    getHttpsHostname(config.getOptionalString('backend.baseUrl')) ===
      expectedHost
  );
}

export function isStaticAuthUiEnabled(config: LocalAuthUiConfig): boolean {
  return isLocalAuthUiEnabled(config) || isDcpDevelopmentAuthUiEnabled(config);
}

function isLocalAuthResponse(value: unknown): value is LocalAuthResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const profile = Reflect.get(value, 'profile');
  const backstageIdentity = Reflect.get(value, 'backstageIdentity');
  if (
    typeof profile !== 'object' ||
    profile === null ||
    typeof backstageIdentity !== 'object' ||
    backstageIdentity === null
  ) {
    return false;
  }

  const identity = Reflect.get(backstageIdentity, 'identity');
  return (
    typeof Reflect.get(backstageIdentity, 'token') === 'string' &&
    typeof identity === 'object' &&
    identity !== null &&
    Reflect.get(identity, 'type') === 'user' &&
    typeof Reflect.get(identity, 'userEntityRef') === 'string' &&
    Array.isArray(Reflect.get(identity, 'ownershipEntityRefs'))
  );
}

function tokenExpiresSoon(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims: unknown = JSON.parse(globalThis.atob(base64));
    const expiresAt =
      typeof claims === 'object' && claims !== null
        ? Reflect.get(claims, 'exp')
        : undefined;

    return (
      typeof expiresAt !== 'number' ||
      expiresAt * 1000 <= Date.now() + TOKEN_REFRESH_MARGIN_MS
    );
  } catch {
    return true;
  }
}

async function exchangeLocalCredentials(
  options: LocalAuthRequestOptions,
): Promise<LocalAuthResponse> {
  const authBaseUrl = await options.discoveryApi.getBaseUrl('auth');
  const response = await (options.fetch ?? globalThis.fetch)(
    `${authBaseUrl}/local/refresh`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: options.username,
        password: options.password,
      }),
    },
  );

  if (!response.ok) {
    throw await ResponseError.fromResponse(response);
  }

  const payload: unknown = await response.json();
  if (!isLocalAuthResponse(payload)) {
    throw new Error('Local auth returned an invalid identity response');
  }

  return payload;
}

class LocalUserIdentity implements IdentityApi {
  private response: LocalAuthResponse | undefined;
  private password: string;
  private refreshPromise: Promise<void> | undefined;

  constructor(
    response: LocalAuthResponse,
    private readonly username: string,
    password: string,
    private readonly discoveryApi: Pick<DiscoveryApi, 'getBaseUrl'>,
    private readonly fetch?: typeof globalThis.fetch,
  ) {
    this.response = response;
    this.password = password;
  }

  private getResponse(): LocalAuthResponse {
    if (!this.response) {
      throw new Error('Local auth session has ended');
    }
    return this.response;
  }

  private async refresh(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = exchangeLocalCredentials({
        username: this.username,
        password: this.password,
        discoveryApi: this.discoveryApi,
        fetch: this.fetch,
      })
        .then(response => {
          this.response = response;
        })
        .finally(() => {
          this.refreshPromise = undefined;
        });
    }

    return this.refreshPromise;
  }

  async getProfileInfo(): Promise<ProfileInfo> {
    return this.getResponse().profile;
  }

  async getBackstageIdentity(): Promise<BackstageUserIdentity> {
    return this.getResponse().backstageIdentity.identity;
  }

  async getCredentials(): Promise<{ token: string }> {
    if (tokenExpiresSoon(this.getResponse().backstageIdentity.token)) {
      await this.refresh();
    }

    return { token: this.getResponse().backstageIdentity.token };
  }

  async signOut(): Promise<void> {
    this.password = '';
    this.response = undefined;
  }
}

export async function authenticateLocalUser(
  options: LocalAuthRequestOptions,
): Promise<IdentityApi> {
  const response = await exchangeLocalCredentials(options);
  return new LocalUserIdentity(
    response,
    options.username,
    options.password,
    options.discoveryApi,
    options.fetch,
  );
}
